import {
  useEffect,
  useState,
  useCallback,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, PanelLeft, RefreshCw } from 'lucide-react';
import type {
  Feed,
  Source,
  ActionConfig,
  ActionType,
  KeywordFilterConfig,
  AIFilterConfig,
  SummarizeConfig,
  TranslateConfig,
} from '@new-signal/shared';
import { apiFetch } from '../lib/api';
import { track } from '../lib/analytics';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { cn } from '../lib/utils';
import { FeedConfigPanel } from '../components/reader/FeedConfigPanel';
import {
  ArticleListPanel,
  type Article,
} from '../components/reader/ArticleListPanel';
import { ArticleContentPanel } from '../components/reader/ArticleContentPanel';

const COL1_COLLAPSED_KEY = 'signal_col1_collapsed';

interface ArticlesResponse {
  articles: Article[];
  total: number;
  page: number;
  has_more: boolean;
  last_synced_at: string | null;
}

function formatRelative(
  iso: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const minutes = Math.floor((Date.now() - then) / 60_000);
  if (minutes < 1) return t('preview.justNow');
  if (minutes < 60) return t('preview.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('preview.hoursAgo', { count: hours });
  return t('preview.daysAgo', { count: Math.floor(hours / 24) });
}

function isValidUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function getActionValidationErrors(
  actions: ActionConfig[],
  t: (key: string) => string,
): Record<number, string> {
  const errors: Record<number, string> = {};
  let hasPreviousSummarize = false;

  actions.forEach((action, i) => {
    if (action.type === 'keyword_filter' && action.keywords.length === 0) {
      errors[i] = t('keywordFilter.requiredError');
    }
    if (action.type === 'ai_filter' && action.description.length < 10) {
      errors[i] = t('aiFilter.requiredError');
    }
    if (action.type === 'translate' && action.source === 'summary' && !hasPreviousSummarize) {
      errors[i] = t('translate.summarySourceRequiresSummarize');
    }
    if (action.type === 'summarize') {
      hasPreviousSummarize = true;
    }
  });

  return errors;
}

export function FeedDetailPage() {
  const { t } = useTranslation('feed-builder');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [feed, setFeed] = useState<Feed | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);

  const [url, setUrl] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [actions, setActions] = useState<ActionConfig[]>([]);
  const [actionsDirty, setActionsDirty] = useState(false);
  const [savingActions, setSavingActions] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [actionValidationErrors, setActionValidationErrors] = useState<Record<number, string>>({});

  const [configCollapsed, setConfigCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COL1_COLLAPSED_KEY) === '1';
  });

  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const [articles, setArticles] = useState<Article[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [articlesError, setArticlesError] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COL1_COLLAPSED_KEY, configCollapsed ? '1' : '0');
  }, [configCollapsed]);

  useEffect(() => {
    if (!feed) return;
    track('output_workspace_opened', { feed_id: feed.id });
  }, [feed?.id]);

  const loadFeed = useCallback(async () => {
    const [feedRes, sourcesRes] = await Promise.all([
      apiFetch(`/feeds/${id}`),
      apiFetch(`/feeds/${id}/sources`),
    ]);

    if (feedRes.ok) {
      const feedData = (await feedRes.json()) as Feed;
      setFeed(feedData);
      setEditName(feedData.name);
      setActions(feedData.actions ?? []);
      setActionsDirty(false);
    } else {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (sourcesRes.ok) {
      setSources((await sourcesRes.json()) as Source[]);
    }

    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const loadArticles = useCallback(
    async (mode: 'initial' | 'background') => {
      if (mode === 'initial') setArticlesLoading(true);
      setArticlesError(false);

      try {
        const res = await apiFetch(`/feeds/${id}/articles`);
        if (!res.ok) {
          setArticlesError(true);
          return;
        }
        const body = (await res.json()) as ArticlesResponse;
        setArticles(body.articles);
        setLastSyncedAt(body.last_synced_at);
      } catch {
        setArticlesError(true);
      } finally {
        setArticlesLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    loadArticles('initial');
  }, [loadArticles]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setArticlesError(false);
    track('output_manual_sync_clicked', { feed_id: id });

    try {
      const syncRes = await apiFetch(`/feeds/${id}/sync`, { method: 'POST' });
      if (!syncRes.ok) {
        setArticlesError(true);
        track('output_manual_sync_failed', { feed_id: id });
        return;
      }
      await loadArticles('background');
      track('output_manual_sync_completed', { feed_id: id });
      setSelectedArticle(null);
    } catch {
      setArticlesError(true);
      track('output_manual_sync_failed', { feed_id: id });
    } finally {
      setSyncing(false);
    }
  };

  const handleRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!editName.trim()) {
      setRenameError(t('create.step1.error'));
      return;
    }
    if (editName.trim() === feed?.name) {
      setRenameError(null);
      setEditing(false);
      return;
    }
    setRenameError(null);
    setRenaming(true);

    const res = await apiFetch(`/feeds/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name: editName.trim() }),
    });

    if (res.ok) {
      const updated = (await res.json()) as Feed;
      setFeed(updated);
      setEditName(updated.name);
      setEditing(false);
    } else {
      const body = await res.json().catch(() => null);
      setRenameError((body as { error?: string })?.error ?? tc('error.generic'));
    }

    setRenaming(false);
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setEditing(false);
      setEditName(feed?.name ?? '');
      setRenameError(null);
    }
  };

  const handleAddSource = async (e: FormEvent) => {
    e.preventDefault();
    setAddError(null);

    const trimmed = url.trim();
    if (!trimmed) return;

    if (!isValidUrl(trimmed)) {
      setAddError(t('detail.urlError'));
      return;
    }

    setAdding(true);

    const res = await apiFetch(`/feeds/${id}/sources`, {
      method: 'POST',
      body: JSON.stringify({ url: trimmed }),
    });

    if (res.ok) {
      const source = (await res.json()) as Source;
      setSources((prev) => [...prev, source]);
      track('source_added', { total_sources: sources.length + 1 });
      track('feed_tuned_from_output_workspace', {
        feed_id: id,
        changed_fields: ['sources'],
      });
      setUrl('');
    } else {
      const body = await res.json().catch(() => null);
      setAddError((body as { error?: string })?.error ?? tc('error.generic'));
    }

    setAdding(false);
  };

  const handleRemoveSource = async (srcId: string) => {
    const res = await apiFetch(`/feeds/${id}/sources/${srcId}`, { method: 'DELETE' });
    if (res.ok) {
      setSources((prev) => prev.filter((s) => s.id !== srcId));
      track('source_removed', { total_sources: sources.length - 1 });
      track('feed_tuned_from_output_workspace', {
        feed_id: id,
        changed_fields: ['sources'],
      });
    }
  };

  const handleAddAction = (type: ActionType) => {
    if (type === 'keyword_filter') {
      const newAction: KeywordFilterConfig = { type: 'keyword_filter', mode: 'include', keywords: [] };
      setActions((prev) => [...prev, newAction]);
      setActionsDirty(true);
    }
    if (type === 'ai_filter') {
      const newAction: AIFilterConfig = { type: 'ai_filter', description: '', threshold: 0.7 };
      setActions((prev) => [...prev, newAction]);
      setActionsDirty(true);
    }
    if (type === 'summarize') {
      const newAction: SummarizeConfig = {
        type: 'summarize',
        length: 'short',
        include_read_time: true,
        output: 'prepend_to_content',
      };
      setActions((prev) => [...prev, newAction]);
      setActionsDirty(true);
      track('summarize_action_added', {
        length: newAction.length,
        include_read_time: newAction.include_read_time,
      });
    }
    if (type === 'translate') {
      const newAction: TranslateConfig = {
        type: 'translate',
        target_language: 'en',
        source: actions.some((action) => action.type === 'summarize') ? 'summary' : 'content',
        display: 'bilingual',
      };
      setActions((prev) => [...prev, newAction]);
      setActionsDirty(true);
      track('translate_action_added', {
        target_language: newAction.target_language,
        source: newAction.source,
        display: newAction.display,
      });
    }
  };

  const handleUpdateAction = (index: number, updated: ActionConfig) => {
    setActions((prev) => prev.map((a, i) => (i === index ? updated : a)));
    setActionsDirty(true);
    setActionValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleRemoveAction = (index: number) => {
    const removed = actions[index];
    setActions((prev) => prev.filter((_, i) => i !== index));
    setActionsDirty(true);
    setActionValidationErrors({});
    if (removed?.type === 'summarize') {
      track('summarize_action_removed', {
        length: removed.length,
        include_read_time: removed.include_read_time,
      });
    }
    if (removed?.type === 'translate') {
      track('translate_action_removed', {
        target_language: removed.target_language,
        source: removed.source,
        display: removed.display,
      });
    }
  };

  const handleSaveActions = async () => {
    const errors = getActionValidationErrors(actions, t);
    if (Object.keys(errors).length > 0) {
      setActionValidationErrors(errors);
      return;
    }
    setActionValidationErrors({});

    setSavingActions(true);
    setActionsError(null);

    const res = await apiFetch(`/feeds/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ actions }),
    });

    if (res.ok) {
      const updated = (await res.json()) as Feed;
      setFeed(updated);
      setActions(updated.actions ?? []);
      setActionsDirty(false);
      setSelectedArticle(null);
      loadArticles('background');
      track('feed_tuned_from_output_workspace', {
        feed_id: updated.id,
        changed_fields: ['actions'],
      });
      for (const action of actions) {
        if (action.type === 'summarize') {
          track('summarize_action_saved', {
            length: action.length,
            include_read_time: action.include_read_time,
          });
        }
        if (action.type === 'translate') {
          track('translate_action_saved', {
            target_language: action.target_language,
            source: action.source,
            display: action.display,
          });
        }
      }
    } else {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      setActionsError(body?.error ?? tc('error.generic'));
    }

    setSavingActions(false);
  };

  const handleDiscardChanges = () => {
    if (!feed) return;
    setActions(feed.actions ?? []);
    setActionsDirty(false);
    setActionValidationErrors({});
    setActionsError(null);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-canvas">
        <p className="text-sm text-brand-muted">{tc('loading')}</p>
      </div>
    );
  }

  if (!feed) return null;

  const syncLabel = syncing
    ? t('reader.list.refreshing')
    : lastSyncedAt
      ? t('reader.list.lastSynced', { time: formatRelative(lastSyncedAt, t) })
      : t('reader.list.neverSynced');

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-brand-canvas text-brand-text">
      {/* Unified toolbar */}
      <div className="flex h-[52px] shrink-0 items-center gap-3.5 border-b border-brand-border bg-white/80 px-4 backdrop-blur-[20px]">
        <button
          type="button"
          onClick={() => setConfigCollapsed((v) => !v)}
          aria-label={
            configCollapsed ? t('reader.config.expand') : t('reader.config.collapse')
          }
          aria-pressed={!configCollapsed}
          className="hidden h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-brand-text hover:bg-black/5 md:flex"
        >
          <PanelLeft size={17} strokeWidth={1.6} />
        </button>
        <Link
          to="/dashboard"
          className="flex shrink-0 items-center gap-0.5 text-sm leading-5 text-accent-blue no-underline transition-colors hover:text-accent-blue-hover"
        >
          <ChevronLeft size={13} strokeWidth={2.5} />
          {tc('backToFeeds')}
        </Link>
        <span aria-hidden className="h-5 w-px shrink-0 bg-brand-border-medium" />
        {editing ? (
          <form onSubmit={handleRename} className="flex min-w-0 items-center gap-2">
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={handleRename}
              disabled={renaming}
              autoFocus
              aria-label={t('detail.renameAria')}
              className={cn(
                'min-w-0 border-0 border-b bg-transparent px-0 py-0.5 text-sm font-semibold leading-5 tracking-[-0.01em] text-brand-text outline-none',
                renameError ? 'border-b-brand-error' : 'border-b-accent-blue',
              )}
            />
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={t('detail.renameAria')}
            className="min-w-0 cursor-text truncate border-0 bg-transparent p-0 text-sm font-semibold leading-5 tracking-[-0.01em] text-brand-text hover:opacity-70"
          >
            {feed.name}
          </button>
        )}
        {renameError && (
          <span className="shrink-0 text-[12px] leading-4 text-brand-error">{renameError}</span>
        )}
        <span className="flex-1" />
        <span className="hidden whitespace-nowrap text-xs leading-5 text-brand-muted sm:inline">
          {syncLabel}
        </span>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing || articlesLoading}
          aria-label={t('reader.list.sync')}
          title={syncing ? t('reader.list.refreshing') : t('reader.list.sync')}
          className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-brand-text hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw size={15} strokeWidth={2} className={cn(syncing && 'animate-spin')} />
        </button>
        <LanguageSwitcher />
      </div>

      {/* 3-column layout. Column 1 is hidden below md=768 per ticket — tablet
          viewports show only columns 2 and 3. */}
      <div className="flex min-h-0 flex-1">
        {!configCollapsed && (
          <div className="hidden md:flex">
            <FeedConfigPanel
              sources={sources}
              actions={actions}
              actionsDirty={actionsDirty}
              savingActions={savingActions}
              actionValidationErrors={actionValidationErrors}
              actionsError={actionsError}
              adding={adding}
              addError={addError}
              url={url}
              onUrlChange={setUrl}
              onAddSource={handleAddSource}
              onRemoveSource={handleRemoveSource}
              onAddAction={handleAddAction}
              onUpdateAction={handleUpdateAction}
              onRemoveAction={handleRemoveAction}
              onSaveActions={handleSaveActions}
              onDiscardChanges={handleDiscardChanges}
            />
          </div>
        )}

        <ArticleListPanel
          feedId={feed.id}
          articles={articles}
          loading={articlesLoading}
          error={articlesError}
          syncLabel={syncLabel}
          selectedArticleId={selectedArticle?.id ?? null}
          onSelectArticle={setSelectedArticle}
        />

        <ArticleContentPanel feedId={feed.id} article={selectedArticle} />
      </div>
    </div>
  );
}
